import { Select, Tag } from 'antd';
import { useFormikContext } from 'formik';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BaseLoader from 'components/atoms/base-loader';
import { useMembersForPod } from 'utils/hooks/pods/useMembersForPod';

import MemberAssignmentTable from './member-assignment-table';
import parentS from '../styles.module.scss';
import s from './styles.module.scss';

import type {
  ICreatePodFormValues,
  IMemberFormValue,
} from '../validation-schema';
import type { FC } from 'react';

const DEBOUNCE_MS = 300;

const AssignedMembersSection: FC = () => {
  const { t } = useTranslation();
  const { values, setFieldValue, errors, submitCount } =
    useFormikContext<ICreatePodFormValues>();
  const [memberSearch, setMemberSearch] = useState<string | undefined>();
  const { members: allMembers, isLoading: membersLoading } =
    useMembersForPod(memberSearch);
  const [memberSelectKey, setMemberSelectKey] = useState(0);

  const debouncedMemberSearch = useRef(
    debounce((value: string) => {
      setMemberSearch(value || undefined);
    }, DEBOUNCE_MS),
  ).current;

  useEffect(() => {
    return () => debouncedMemberSearch.cancel();
  }, [debouncedMemberSearch]);

  const assignedGuids = useMemo(
    () => new Set(values.members.map((m) => m.loginGuid)),
    [values.members],
  );

  const memberOptions = useMemo(
    () =>
      allMembers
        .filter((m) => !assignedGuids.has(m.loginGuid))
        .map((m) => ({
          label: `${m.name} - ${m.role}`,
          value: m.loginGuid,
          name: m.name,
          role: m.role,
        })),
    [allMembers, assignedGuids],
  );

  const handleMemberSelect = useCallback(
    (loginGuid: string) => {
      const member = allMembers.find((m) => m.loginGuid === loginGuid);
      if (!member) return;

      const newMember: IMemberFormValue = {
        loginGuid: member.loginGuid,
        name: member.name,
        role: member.role,
        priority: 'primary',
      };

      setFieldValue('members', [...values.members, newMember]);
      // Reset search and remount the Select to show placeholder
      setMemberSearch(undefined);
      setMemberSelectKey((k) => k + 1);
    },
    [allMembers, values.members, setFieldValue],
  );

  const handleRemoveMember = useCallback(
    (loginGuid: string) => {
      setFieldValue(
        'members',
        values.members.filter((m) => m.loginGuid !== loginGuid),
      );
    },
    [values.members, setFieldValue],
  );

  const handlePriorityChange = useCallback(
    (loginGuid: string, priority: 'primary' | 'secondary' | 'tertiary') => {
      setFieldValue(
        'members',
        values.members.map((m) =>
          m.loginGuid === loginGuid ? { ...m, priority } : m,
        ),
      );
    },
    [values.members, setFieldValue],
  );

  const membersError = submitCount > 0 ? (errors.members as string) : undefined;

  return (
    <div className={s.section} data-cy="create-pod__assigned-members">
      <div className={parentS.sectionHeader}>
        <span className={parentS.sectionTitle}>
          {t('labels.assignedMembers')}
        </span>
        <span className={parentS.sectionHelper}>
          {t('labels.assignedMembersHelper')}
        </span>
      </div>

      <div>
        <div className={s.fieldLabel}>
          <span className={s.fieldLabelText}>{t('labels.assignMember')}</span>
          <span className={s.requiredMark}>*</span>
        </div>
        <span data-cy="create-pod__assign-member">
          <Select
            key={memberSelectKey}
            showSearch
            popupClassName={parentS.selectDropdown}
            loading={membersLoading}
            placeholder={t('labels.pleaseSelect')}
            onSelect={handleMemberSelect}
            onSearch={debouncedMemberSearch}
            options={memberOptions}
            optionRender={(option) => (
              <div className={s.memberOption}>
                <span className={s.memberOptionName}>{option.data.name}</span>
                {option.data.role && (
                  <Tag bordered={false} className={s.memberOptionRoleTag}>
                    {option.data.role}
                  </Tag>
                )}
              </div>
            )}
            filterOption={false}
            notFoundContent={
              membersLoading ? (
                <BaseLoader loading height={20} width={20} />
              ) : undefined
            }
            style={{ width: '100%' }}
          />
        </span>
        {membersError && <span className={s.errorText}>{membersError}</span>}
      </div>

      {values.members.length > 0 && (
        <MemberAssignmentTable
          members={values.members}
          onRemove={handleRemoveMember}
          onPriorityChange={handlePriorityChange}
        />
      )}
    </div>
  );
};

export default AssignedMembersSection;
